<template>
  <column1>
    <template v-if="!isNil(hotel)">
      <div id="wcag" @click="onClickWcag">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.--><path d="M118.6 80c-11.5 0-21.4 7.9-24 19.1L57 260.3c20.5-6.2 48.3-12.3 78.7-12.3c32.3 0 61.8 6.9 82.8 13.5c10.6 3.3 19.3 6.7 25.4 9.2c3.1 1.3 5.5 2.4 7.3 3.2c.9 .4 1.6 .7 2.1 1l.6 .3 .2 .1c0 0 .1 0 .1 0c0 0 0 0 0 0s0 0 0 0L247.9 288s0 0 0 0l6.3-12.7c5.8 2.9 10.4 7.3 13.5 12.7l40.6 0c3.1-5.3 7.7-9.8 13.5-12.7l6.3 12.7s0 0 0 0c-6.3-12.7-6.3-12.7-6.3-12.7s0 0 0 0s0 0 0 0c0 0 .1 0 .1 0l.2-.1 .6-.3c.5-.2 1.2-.6 2.1-1c1.8-.8 4.2-1.9 7.3-3.2c6.1-2.6 14.8-5.9 25.4-9.2c21-6.6 50.4-13.5 82.8-13.5c30.4 0 58.2 6.1 78.7 12.3L481.4 99.1c-2.6-11.2-12.6-19.1-24-19.1c-3.1 0-6.2 .6-9.2 1.8L416.9 94.3c-12.3 4.9-26.3-1.1-31.2-13.4s1.1-26.3 13.4-31.2l31.3-12.5c8.6-3.4 17.7-5.2 27-5.2c33.8 0 63.1 23.3 70.8 56.2l43.9 188c1.7 7.3 2.9 14.7 3.5 22.1c.3 1.9 .5 3.8 .5 5.7l0 6.7 0 41.3 0 16c0 61.9-50.1 112-112 112l-44.3 0c-59.4 0-108.5-46.4-111.8-105.8L306.6 352l-37.2 0-1.2 22.2C264.9 433.6 215.8 480 156.3 480L112 480C50.1 480 0 429.9 0 368l0-16 0-41.3L0 304c0-1.9 .2-3.8 .5-5.7c.6-7.4 1.8-14.8 3.5-22.1l43.9-188C55.5 55.3 84.8 32 118.6 32c9.2 0 18.4 1.8 27 5.2l31.3 12.5c12.3 4.9 18.3 18.9 13.4 31.2s-18.9 18.3-31.2 13.4L127.8 81.8c-2.9-1.2-6-1.8-9.2-1.8zM64 325.4L64 368c0 26.5 21.5 48 48 48l44.3 0c25.5 0 46.5-19.9 47.9-45.3l2.5-45.6c-2.3-.8-4.9-1.7-7.5-2.5c-17.2-5.4-39.9-10.5-63.6-10.5c-23.7 0-46.2 5.1-63.2 10.5c-3.1 1-5.9 1.9-8.5 2.9zM512 368l0-42.6c-2.6-.9-5.5-1.9-8.5-2.9c-17-5.4-39.5-10.5-63.2-10.5c-23.7 0-46.4 5.1-63.6 10.5c-2.7 .8-5.2 1.7-7.5 2.5l2.5 45.6c1.4 25.4 22.5 45.3 47.9 45.3l44.3 0c26.5 0 48-21.5 48-48z" /></svg>
        &nbsp;Barrierefreiheit
      </div>
      <loading v-if="skipping" />
      <h1 class="h1 mb-32">
        {{ $t('homeView.headline') }}
      </h1>
      <availability-calendar ref="calendar"
                             :locale="locale"
                             :start-date.sync="arrivalDate"
                             :end-date.sync="departureDate"
                             :hotel="hotelId"
                             :unavailable-color="unavailableColor"
                             :unavailable-text-color="unavailableTextColor"
                             :ari="ari"
                             :availability="overallAvailability"
      />
      <div class="button-backdrop" :title="$t('homeView.headline')" :aria-label="$t('homeView.headline')" role="button" />
      <button-floating v-if="isNil(arrivalDate)" :disabled="true">
        {{ $t('homeView.selectArrivalDate') }}
      </button-floating>
      <button-floating v-else-if="!isNil(arrivalDate) && isNil(departureDate)"
                       :disabled="true"
                       :clearable="true"
                       :clear-label="$t('homeView.clearSelection')"
                       @clear="onClearSelection"
      >
        {{ $t('homeView.selectDepartureDate') }}
      </button-floating>
      <button-floating v-else
                       :clearable="true"
                       :clear-label="$t('homeView.clearSelection')"
                       @clear="onClearSelection"
                       @click.stop="onClickNext"
      >
        {{ $t('homeView.next', [formattedDateRange]) }}
      </button-floating>
    </template>
  </column1>
</template>

<script>
import AvailabilityCalendar from '@/components/lib/calendar/AvailabilityCalendar';
import ButtonFloating from '@/components/lib/ButtonFloating/ButtonFloating';
import Column1 from '@/components/lib/Layout/Column1';
import BookingEngineMixin from '@/mixins/BookingEngineMixin';
import { throttle } from 'lodash/function';
import { v4 as uuidv4 } from 'uuid';
import { keyBy } from 'lodash/collection';
import { isEmpty, isNil } from 'lodash/lang';
import DateRangeMixin from '@/mixins/DateRangeMixin';
import Cookies from 'js-cookie';
import fecha from 'fecha';
import { replace } from 'lodash/string';
import shortid from 'shortid';
import Loading from '@/components/lib/Loading';
import { getOrCreateUid, getExistingUid } from '@/lib/SessionUid';

export default {
  name: 'HomeView',
  components: {
    Column1,
    'availability-calendar': AvailabilityCalendar,
    'button-floating': ButtonFloating,
    'loading': Loading,
  },

  mixins: [BookingEngineMixin, DateRangeMixin],

  props: {
    msg: String,
  },

  data: function () {
    return {
      ari: null,
      arrivalDate: null,
      departureDate: null,
      dateSelectionUuid: uuidv4(),
      throttledUpdateAvailability: throttle(this.updateAvailability, 1000, { leading: true, trailing: false }),
      skipping: false,
      overallAvailability: null,
    };
  },

  computed: {
    hotelId: function () {
      return this.$route.params.encodedHotelId;
    },
    maxRoomsAvailable() {
      return this.$store.getters.maxRoomsAvailable;
    },
    unavailableColor() {
      if (!isNil(this.hotel)) {
        switch (this.hotel.hotel.whiteLabel) {
          case 'Rainmaker': return '#eccccd';
          default: return '#ede8e8';
        }
      }
      return '#ede8e8';
    },
    unavailableTextColor() {
      if (!isNil(this.hotel)) {
        switch (this.hotel.hotel.whiteLabel) {
          case 'Rainmaker': return '#a1abb3';
          default: return '#bfcfdb';
        }
      }
      return '#bfcfdb';
    },
  },

  watch: {
    arrivalDate(newVal, oldVal) {
      this.$store.commit('arrivalDate', newVal);
    },
    departureDate(newVal, oldVal) {
      // this.$serviceBus.$emit('infoBox.hide');
      this.dateSelectionUuid = uuidv4();
      if (isNil(newVal)) {
        this.$store.dispatch('roomTypeAvailability', null);
        this.$store.commit('departureDate', null);
      } else {
        this.$store.commit('departureDate', newVal);
        this.throttledUpdateAvailability(this.dateSelectionUuid);
      }
    },
    maxRoomsAvailable: function (newVal) {
      if (newVal === 0) {
        this.$serviceBus.$emit('infoBox.msg', this.$t('availabilityCalendar.infoMsgNoRoomsAvailable'));
      }
    },
  },

  created: function () {
    if (!isEmpty(this.$route.query) && !isEmpty(this.$route.query.arrivalDate) && !isEmpty(this.$route.query.departureDate)) {
      try {
        const arrivalDate = this.$route.query.arrivalDate.includes('-')
          ? fecha.format(fecha.parse(this.$route.query.arrivalDate, 'YYYY-MM-DD'), 'YYYYMMDD')
          : fecha.format(fecha.parse(this.$route.query.arrivalDate, 'YYYYMMDD'), 'YYYYMMDD');

        const departureDate = this.$route.query.departureDate.includes('-')
          ? fecha.format(fecha.parse(this.$route.query.departureDate, 'YYYY-MM-DD'), 'YYYYMMDD')
          : fecha.format(fecha.parse(this.$route.query.departureDate, 'YYYYMMDD'), 'YYYYMMDD');

        this.$store.commit('arrivalDate', arrivalDate);
        this.$store.commit('departureDate', departureDate);
      } catch (e) { /* ignore invalid date errors */ }
    }
    if (!isEmpty(this.$route.query) && !isEmpty(this.$route.query.roomTypeId)) {
      this.$store.commit('preferredRoomTypeId', this.$route.query.roomTypeId);
    }
    if (!isEmpty(this.$route.query) && !isEmpty(this.$route.query.ratePlanId)) {
      this.$store.commit('preferredRatePlanId', this.$route.query.ratePlanId);
    }
    if (!isEmpty(this.$route.query) && !isEmpty(this.$route.query.discountCode)) {
      this.$store.commit('discountCode', this.$route.query.discountCode);
    }
    if (!isEmpty(this.$route.query) && !isEmpty(this.$route.query.lang)) {
      this.changeLocale(this.$route.query.lang);
    }
    if (!isEmpty(this.$route.query) && !isEmpty(this.$route.query.numAdults)) {
      const roomRequests = [];
      for (let i = 1; i <= (this.$route.query.roomCount || 1); i++) {
        roomRequests.push({
          id: shortid.generate(),
          numAdults: parseInt(this.$route.query.numAdults, 10),
          childAgeCategories: [],
        });
        this.$store.dispatch('roomRequests', roomRequests);
      }
    }

    if (!isEmpty(this.$route.query) && !isEmpty(this.$route.query.source)) {
      const hotelId = this.$route.params.encodedHotelId;
      const source = this.$route.query.source;

      switch (source) {
        case 'localuniversal':
          this.$store.commit('sourceName', 'Google Hotel (Search)');
          break;
        case 'mapresults':
          this.$store.commit('sourceName', 'Google Hotel (Map)');
          break;
        default:
          this.$store.commit('sourceName', source);
          break;
      }
    }

    this.arrivalDate = this.$store.getters.arrivalDate;
    this.departureDate = this.$store.getters.departureDate;

    if (!isEmpty(this.$route.query) && !isEmpty(this.$route.query.skip)) {
      if (!isNil(this.arrivalDate) && !isNil(this.departureDate)) {
        if (!isEmpty(this.$route.query.numAdults)) {
          this.updateAvailability(this.dateSelectionUuid).then(() => {
            window.scroll({ top: 0, behavior: 'smooth' });
            this.skipping = true;
            this._quote()
              .then(() => {
                const roomRequestId = this.roomRequests[0].id;
                this.$router.push({ name: 'roomselect', params: { roomRequestId: roomRequestId } });
              })
              .finally(() => {
                this.skipping = false;
              });
          });
        } else {
          this.updateAvailability(this.dateSelectionUuid).then(() => {
            window.scroll({ top: 0, behavior: 'smooth' });
            this.$router.push({ name: 'roomcount' });
          });
        }
      }
    } else {
      this.throttledUpdateAvailability.flush(this.dateSelectionUuid);
    }

    // Session ID for meta-channel conversion billing — never allowed to break IBE
    try {
      const channelParams = {};
      if (!isEmpty(this.$route.query.trv_reference)) {
        channelParams.trv_reference = this.$route.query.trv_reference;
      }
      // DSGVO: create a new UID only when a meta-channel param is present (= concrete purpose).
      // For direct/organic visits, only read an existing UID (may have been set on a prior
      // meta-channel visit) so that a returning user can still be attributed.
      const uid = !isEmpty(channelParams) ? getOrCreateUid() : getExistingUid();
      if (uid) {
        this.$store.commit('sessionId', uid);
        if (!isEmpty(channelParams)) {
          this.$superagent
            .post(this.serviceUrls.hotel.upsertSession(this.hotelId))
            .send({ sessionId: uid, params: channelParams })
            .catch(() => { /* ignore - non-critical */ });
        }
      }
    } catch (_) { /* never let session handling break the IBE */ }

    this.updateARI();
    this.updateOverallAvailability();
  },

  mounted: function () {
    this.$nextTick(() => {
      let startElement = document.querySelector('.selected--start');
      if (!isNil(startElement)) startElement = startElement.closest('.month');
      if (!isNil(startElement)) {
        window.scroll({
          // 52 is to adjust for the fixed weekday header
          top: window.scrollY + startElement.getBoundingClientRect().top - 52,
          behavior: 'smooth',
        });
      }
    });
  },

  methods: {
    updateAvailability(dateSelectionUuid) {
      if (dateSelectionUuid !== this.dateSelectionUuid) return Promise.resolve();

      if (isNil(this.arrivalDate)) Promise.resolve();
      if (isNil(this.departureDate)) Promise.resolve();
      if (this.arrivalDate === this.departureDate) return Promise.resolve();
      if (this.departureDate <= this.arrivalDate) return Promise.resolve();

      // to prevent issues when this object is destroyed once the service call finishes
      const $store = this.$store;

      return this.$superagent
        .get(this.serviceUrls.hotel.availability(this.hotelId))
        .query(`locale=${this.locale}&arrivalDate=${this.arrivalDate}&departureDate=${this.departureDate}`)
        .then((res) => {
          const data = keyBy(res.body.data, (it) => it.roomTypeId);
          if (dateSelectionUuid === this.dateSelectionUuid) {
            $store.dispatch('roomTypeAvailability', data);
            this.$serviceBus.$emit('quote'); // update quote in case the availability change affected a previously stored quote
          }
        })
        .catch((err) => {
          if (dateSelectionUuid === this.dateSelectionUuid) $store.dispatch('roomTypeAvailability', null);
          this.$serviceBus.$emit('infoBox.msg', this.$t('errors.failedToQueryAvailability'));
        });
    },
    updateOverallAvailability() {
      return this.$superagent
        .get(this.serviceUrls.hotel.overallAvailability(this.hotelId))
        .query(`locale=${this.locale}`)
        .then((res) => {
          this.overallAvailability = keyBy(res.body, (it) => it.id);
        })
        .catch((err) => {
          this.overallAvailability = null;
        });
    },
    addDays(date, days) {
      const ret = new Date(date);
      ret.setDate(ret.getDate() + days);
      return ret;
    },
    updateARI(dateSelectionUuid) {
      if (this.$store.getters.hotel.details.onlineBookingSettings.extendedAriValidation === true) {
        this.$superagent
          .get(this.serviceUrls.hotel.ari(this.hotelId))
          .query(`locale=${this.locale}`)
          .then((res) => {
            this.ari = res.body.data;
            // if (dateSelectionUuid === this.dateSelectionUuid) $store.dispatch('roomTypeAvailability', data);
          })
          .catch((err) => {
            // if (dateSelectionUuid === this.dateSelectionUuid) $store.dispatch('roomTypeAvailability', null);
            // this.$serviceBus.$emit('infoBox.msg', this.$t('errors.failedToQueryAvailability'));
          });
      } else {
        this.ari = null;
      }
    },
    onClickNext(event) {
      this.throttledUpdateAvailability.flush(this.dateSelectionUuid)
        .then(() => {
          window.scroll({ top: 0, behavior: 'smooth' });
          // this.gtagTrack('event', 'search', { event_category: 'engagement', event_label: this.formattedDateRange });
          this.$router.push({ name: 'roomcount' });
        });
    },
    onClickWcag(event) {
      this.$serviceBus.$emit('wcagModal.show');
    },
    onClearSelection() {
      // explicit user action – clears the calendar including its remembered
      // confirmed selection (stray clicks outside the calendar no longer do)
      this.$refs.calendar.cancelSelection();
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.button-backdrop {
  @apply fixed bottom-0 left-0 h-128 z-20;
  width: calc(100% + 2 * 64px);
  background: rgb(255,255,255);
  background: linear-gradient(0deg, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 100%);
  @media (min-width: 768px) {
    @apply fixed left-460;
    width: calc(100% - 460px - 64px + 2 * 64px);
    margin-left: -64px;
    max-width: calc(57.2rem + 2 * 6.4rem);
  }
}

#wcag {
  position: absolute;
  right: 0px;
  height: 40px;
  background: var(--text-color);
  width: 60px;
  top: 16px;
  border-radius: 20px 0 0 20px;
  color: white;
  padding: 7px 20px;
  cursor: pointer;
  transition: width ease 0.3s, box-shadow  ease 0.3s;
  display: grid;
  grid-template-columns: min-content auto;
  grid-column-gap: 1rem;
  overflow: hidden;
  width: 250px;

  @media(any-pointer: fine) {
    width: 60px;
  }

  svg {
    height: 24px;
    width: auto;
    stroke: #fff;
    fill: #fff;
  }

  &:hover {
    box-shadow: 0px 2px 6px -1px var(--text-color);
    width: 250px;
  }
}
</style>

<style lang="scss">
.high-contrast {
  --gray: #677580!important;
  --color-primary: #000000!important;

  .text-error {
    color: #d13b3c;
  }
  .day {
    &.past,&.disabled {
      .day--text {
        color: #4b6d85;
      }
    }
  }
}

</style>
